/**
 * Document types for upload + Documents table labels.
 * Keep in sync with src/documents/document-upload-types.ts
 *
 * @typedef {{ value: string, label: string, required: boolean, fmcsaRef: string, group: string }} DocumentTypeDef
 */

/** @type {DocumentTypeDef[]} */
export const DOCUMENT_TYPES_FOR_UPLOAD = [
  // Driver qualification (49 CFR Part 391)
  {
    value: 'medical_card',
    label: "Medical examiner's certificate",
    required: true,
    fmcsaRef: '49 CFR §391.43–391.45',
    group: 'Driver qualification',
  },
  {
    value: 'cdl',
    label: "Commercial driver's license (CDL)",
    required: true,
    fmcsaRef: '49 CFR §391.11(b)(2)',
    group: 'Driver qualification',
  },
  {
    value: 'mvr',
    label: 'Motor vehicle record (MVR)',
    required: true,
    fmcsaRef: '49 CFR §391.23',
    group: 'Driver qualification',
  },
  {
    value: 'drug_test',
    label: 'Drug & alcohol test / Clearinghouse record',
    required: true,
    fmcsaRef: '49 CFR §382.301',
    group: 'Driver qualification',
  },
  {
    value: 'road_test',
    label: 'Road test certificate',
    required: false,
    fmcsaRef: '49 CFR §391.31',
    group: 'Driver qualification',
  },
  {
    value: 'employment_application',
    label: 'Employment application',
    required: false,
    fmcsaRef: '49 CFR §391.21',
    group: 'Driver qualification',
  },
  {
    value: 'annual_violations_cert',
    label: 'Annual certificate of violations',
    required: false,
    fmcsaRef: '49 CFR §391.27',
    group: 'Driver qualification',
  },
  {
    value: 'annual_driving_review',
    label: 'Annual review of driving record',
    required: false,
    fmcsaRef: '49 CFR §391.25',
    group: 'Driver qualification',
  },
  {
    value: 'license',
    label: "Driver's license / permit (non-CDL)",
    required: false,
    fmcsaRef: 'State / 49 CFR §391.11',
    group: 'Driver qualification',
  },
  // Vehicle
  {
    value: 'registration',
    label: 'Vehicle registration',
    required: true,
    fmcsaRef: '49 CFR §393–396',
    group: 'Vehicle',
  },
  {
    value: 'inspection',
    label: 'Annual vehicle inspection',
    required: true,
    fmcsaRef: '49 CFR §396.17',
    group: 'Vehicle',
  },
  {
    value: 'lease_agreement',
    label: 'Vehicle lease / rental agreement',
    required: false,
    fmcsaRef: '49 CFR §376 / lease rules',
    group: 'Vehicle',
  },
  // Carrier & authority
  {
    value: 'insurance',
    label: 'Liability insurance certificate (BIPD / cargo)',
    required: true,
    fmcsaRef: '49 CFR §387',
    group: 'Carrier & authority',
  },
  {
    value: 'ucr',
    label: 'UCR registration / receipt',
    required: true,
    fmcsaRef: 'UCR Act / FMCSA',
    group: 'Carrier & authority',
  },
  {
    value: 'authority_certificate',
    label: 'Operating authority (MC) letter / grant',
    required: false,
    fmcsaRef: '49 CFR §365–368',
    group: 'Carrier & authority',
  },
  {
    value: 'mcs150_confirmation',
    label: 'MCS-150 / biennial update confirmation',
    required: false,
    fmcsaRef: '49 CFR §390.19',
    group: 'Carrier & authority',
  },
  // Tax & operations
  {
    value: 'ifta_license',
    label: 'IFTA credentials / decals',
    required: false,
    fmcsaRef: 'IFTA / state fuel tax',
    group: 'Tax & operations',
  },
  {
    value: 'accident_register',
    label: 'Accident register / summary',
    required: false,
    fmcsaRef: '49 CFR §390.15',
    group: 'Tax & operations',
  },
  {
    value: 'other',
    label: 'Other',
    required: false,
    fmcsaRef: '—',
    group: 'Other',
  },
];

export const DOC_TYPE_LABELS = Object.fromEntries(
  DOCUMENT_TYPES_FOR_UPLOAD.map((t) => [t.value, t.label]),
);

export const DOC_TYPE_REQUIRED = Object.fromEntries(
  DOCUMENT_TYPES_FOR_UPLOAD.map((t) => [t.value, t.required]),
);

export const FMCSA_REF = Object.fromEntries(
  DOCUMENT_TYPES_FOR_UPLOAD.map((t) => [t.value, t.fmcsaRef]),
);

const GROUP_ORDER = [
  'Driver qualification',
  'Vehicle',
  'Carrier & authority',
  'Tax & operations',
  'Other',
];

/** Options grouped for <select><optgroup> */
export function getDocumentTypeSelectGroups() {
  return GROUP_ORDER.map((group) => ({
    group,
    options: DOCUMENT_TYPES_FOR_UPLOAD.filter((t) => t.group === group),
  })).filter((g) => g.options.length > 0);
}
